import React from 'react'
import PropTypes from 'prop-types'
import { createContext } from 'src/util'
import useHandler from 'src/hooks/useHandler'
import {
  Notification,
  LocalizedText,
  Loader,
  CheckIcon,
  TimesIcon,
  InfoCircleIcon,
  ExclamationTriangleIcon,
} from 'src/components'
import styles from './NotificationsContext.scss'

/**
 * Context for managing notifications.
 */
export default createContext(Provider => {
  function NotificationsContext({ transitionDuration = 500, children }) {
    // create dictionnary of notifications in a state
    const [notifications, setNotifications] = React.useState({})

    // keep trace of last notification id
    const count = React.useRef(1)

    const deleteNotification = useHandler(id => {
      const copy = { ...notifications }
      delete copy[id]
      setNotifications(copy)
    })

    /**
     * Handler used to close a notification and to program its deletion
     * @param {Number} id - the notification id to close
     */
    const closeNotification = useHandler(id => {
      if (id) {
        notify({ closed: true }, id)
        setTimeout(() => deleteNotification(id), transitionDuration)
      }
    })

    /**
     * Handler used to create a new notification or to update an existing one if an id is provided
     */
    const notify = useHandler(
      (props, id = count.current++) =>
        setNotifications({
          ...notifications,
          [id]: { transitionDuration, ...notifications[id], ...props, onClose: () => closeNotification(id) },
        }) || id
    )

    /**
     * Spawn a success notification
     */
    const notifySuccess = useHandler((props, id) =>
      notify(
        {
          title: <LocalizedText id="WEB_SUCCESS" />,
          icon: CheckIcon,
          color: 'success',
          duration: undefined,
          ...props,
        },
        id
      )
    )

    /**
     * Spawn an error notification
     */
    const notifyError = useHandler((props, id) =>
      notify(
        {
          title: <LocalizedText id="WEB_ERROR" />,
          icon: TimesIcon,
          color: 'danger',
          duration: undefined,
          ...props,
        },
        id
      )
    )

    /**
     * Spawn a warning notification
     */
    const notifyWarning = useHandler((props, id) =>
      notify(
        {
          title: <LocalizedText id="WEB_WARNING" />,
          icon: ExclamationTriangleIcon,
          color: 'warning',
          duration: undefined,
          ...props,
        },
        id
      )
    )

    /**
     * Spawn an info notification
     */
    const notifyInfo = useHandler((props, id) =>
      notify(
        {
          title: <LocalizedText id="WEB_INFO" />,
          icon: InfoCircleIcon,
          color: 'info',
          duration: undefined,
          ...props,
        },
        id
      )
    )

    /**
     * Spawn a loading notification (info with spinner icon)
     */
    const notifyLoading = useHandler((props, id) =>
      notify(
        {
          title: <LocalizedText id="WEB_LOADING" />,
          icon: Loader,
          color: 'info',
          duration: 0,
          ...props,
        },
        id
      )
    )

    return (
      <Provider
        value={{ notify, notifySuccess, notifyError, notifyWarning, notifyInfo, notifyLoading, closeNotification }}
      >
        {children}
        <div className={styles.container}>
          {Object.entries(notifications).map(([id, props]) => (
            <Notification key={id} {...props} />
          ))}
        </div>
      </Provider>
    )
  }

  NotificationsContext.propTypes = {
    children: PropTypes.node,
    transitionDuration: PropTypes.number,
  }

  return NotificationsContext
}, {})
